/**
 * Module dependencies.
 */

import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';

/**
 * Query key.
 */

const KEY = 'movement-shares';

/**
 * A share link (owner view — never includes the PIN).
 */

export type MovementShare = {
  id: string;
  accountId: number;
  label: string | null;
  hasPin: boolean;
  pin: string | null;
  fromDate: string | null;
  toDate: string | null;
  fields: string[];
  filters: Record<string, any> | null;
  expiresAt: string | null;
  revokedAt: string | null;
  lastAccessAt: string | null;
  createdAt: string;
};

/**
 * Payload to create a share.
 */

export type CreateMovementSharePayload = {
  accountId: number;
  pin?: string;
  label?: string;
  fromDate?: string;
  toDate?: string;
  fields?: string[];
  applyFilters?: boolean;
  filters?: Record<string, any>;
  expiresAt?: string;
};

/**
 * List the account's share links.
 */

export function useMovementShares(accountId?: number) {
  const client = useRequestProvider();

  return useQuery<MovementShare[]>([KEY, accountId], {
    enabled: !!accountId,
    queryFn: async () =>
      client.get(resolveAppUrl('movementShares', { params: { accountId } })).then(({ data }: any) => data?.data)
  });
}

/**
 * Create a share link.
 */

export function useCreateMovementShare() {
  const client = useRequestProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateMovementSharePayload) =>
      client.post(resolveAppUrl('movementShares'), payload).then(({ data }: any) => data?.data as MovementShare),
    onSuccess: () => queryClient.invalidateQueries([KEY])
  });
}

/**
 * Revoke a share link.
 */

export function useRevokeMovementShare() {
  const client = useRequestProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) =>
      client
        .delete(resolveAppUrl('movementShareDelete', { interpolations: { id } }))
        .then(({ data }: any) => data?.data),
    onSuccess: () => queryClient.invalidateQueries([KEY])
  });
}

/**
 * Public share metadata (anonymous — no auth). Drives the PIN prompt / header.
 */

export type PublicShareMeta = {
  available: boolean;
  reason?: string;
  requiresPin?: boolean;
  accountName?: string | null;
  coinId?: string | null;
  label?: string | null;
  fields?: string[];
  fromDate?: string | null;
  toDate?: string | null;
  hasFilters?: boolean;
};

export function usePublicMovementShareMeta(token: string) {
  const client = useRequestProvider();

  return useQuery<PublicShareMeta>([KEY, 'public-meta', token], {
    enabled: !!token,
    retry: false,
    queryFn: async () =>
      client
        .get(resolveAppUrl('publicMovementShare', { interpolations: { token } }))
        .then(({ data }: any) => data?.data)
  });
}

/**
 * A movement row in a public share.
 */

export type PublicShareMovement = {
  id: number;
  date: string;
  bankDate: string | null;
  description: string | null;
  amount: string;
  previousBalance: string;
  balance: string;
  category: string | null;
  subCategory: string | null;
};

/**
 * Result of requesting a public share's movements.
 */

export type PublicShareResult = {
  ok: boolean;
  reason?: string;
  attemptsLeft?: number;
  lockedUntil?: string;
  account?: { name: string | null; coinId: string | null };
  label?: string | null;
  fields?: string[];
  fromDate?: string | null;
  toDate?: string | null;
  movements?: PublicShareMovement[];
};

/**
 * Fetch a public share's movements (anonymous — PIN sent in the POST body).
 */

export function usePublicMovementShare() {
  const client = useRequestProvider();

  return useMutation({
    mutationFn: async ({ token, pin }: { token: string; pin?: string }) =>
      client
        .post(resolveAppUrl('publicMovementShare', { interpolations: { token } }), { pin })
        .then(({ data }: any) => data?.data as PublicShareResult)
  });
}
